import { useCallback, useEffect, useRef, useState } from 'react';

// Shared "expired pre-signed URL" recovery for <img> style assets (avatars, ticket attachments).
//
// The private bucket hands out pre-signed URLs that die after ~1 hour. When an image fails to load,
// the caller's `refetchUrl` re-requests the owning API record (e.g. /auth/me, /account/me,
// /support/tickets/:id) and returns the NEW signed URL. We swap it in and let the image retry
// exactly ONCE. If the refreshed URL also fails (or nothing could be refreshed) we stop and expose
// `failed` so the caller can render its existing placeholder.
//
// The fresh URL lives only in this hook's in-memory state: it is never persisted and never pushed
// back into the caller's state, so a refreshed URL that also fails cannot re-arm the retry.
//
// Audio has its own pre-expiry scheduler (see utils/signedUrlRefresh.js + hooks/useAudioCache.jsx);
// this helper is the reactive, on-error counterpart for images.

const inflight = new Map();

/**
 * Share one in-flight request between callers that use the same key, so N failing images that
 * depend on the same API record trigger a single refetch.
 */
export function shareInflight(key, request) {
  if (!key) return Promise.resolve().then(request);
  if (inflight.has(key)) return inflight.get(key);
  const promise = Promise.resolve()
    .then(request)
    .finally(() => {
      if (inflight.get(key) === promise) inflight.delete(key);
    });
  inflight.set(key, promise);
  return promise;
}

// data:/blob: URLs are local previews; refetching the API cannot fix them.
const isRefreshableUrl = (url) => !!url && !/^(data|blob):/i.test(url);

/**
 * @param {string} sourceUrl  URL currently provided by the caller's state/props.
 *        A new value (new upload, list reload...) starts from a clean slate with a fresh retry.
 * @param {(failedUrl: string) => Promise<string|null|undefined>} refetchUrl
 *        Refetch the owning record and resolve the new signed URL (falsy when unavailable).
 * @returns {{
 *   src: string|undefined,   URL to render (undefined once failed / when there is none)
 *   failed: boolean,         refreshed URL failed too (or refresh was impossible): show fallback
 *   refreshing: boolean,     a refetch is in flight
 *   onError: () => void,     for <img onError>
 *   onLoadingStatusChange: (status: string) => void,  for Radix <AvatarImage>
 * }}
 */
export function useSignedUrlRetry(sourceUrl, refetchUrl) {
  const [state, setState] = useState({
    url: sourceUrl || '',
    failed: false,
    refreshing: false,
  });

  const stateRef = useRef(state);
  stateRef.current = state;
  const attemptedRef = useRef(false); // the single retry has been used
  const mountedRef = useRef(true);
  const refetchRef = useRef(refetchUrl);
  refetchRef.current = refetchUrl;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    attemptedRef.current = false;
    const nextUrl = sourceUrl || '';
    setState((prev) =>
      prev.url === nextUrl && !prev.failed && !prev.refreshing
        ? prev
        : { url: nextUrl, failed: false, refreshing: false },
    );
  }, [sourceUrl]);

  const onError = useCallback(async () => {
    const { url: failedUrl, failed, refreshing } = stateRef.current;
    if (!failedUrl || failed || refreshing) return;

    if (!isRefreshableUrl(failedUrl) || attemptedRef.current) {
      setState((prev) => ({ ...prev, failed: true, refreshing: false }));
      return;
    }

    attemptedRef.current = true;
    stateRef.current = { ...stateRef.current, refreshing: true };
    setState((prev) => ({ ...prev, refreshing: true }));

    let freshUrl = null;
    try {
      freshUrl = await refetchRef.current(failedUrl);
    } catch {
      freshUrl = null;
    }

    // Unmounted, or the caller moved on to a different URL while we were fetching.
    if (!mountedRef.current || stateRef.current.url !== failedUrl) return;

    if (!freshUrl || freshUrl === failedUrl) {
      setState({ url: failedUrl, failed: true, refreshing: false });
      return;
    }
    setState({ url: freshUrl, failed: false, refreshing: false });
  }, []);

  const onLoadingStatusChange = useCallback(
    (status) => {
      if (status === 'error') onError();
    },
    [onError],
  );

  return {
    src: state.failed || !state.url ? undefined : state.url,
    failed: state.failed,
    refreshing: state.refreshing,
    onError,
    onLoadingStatusChange,
  };
}
